import React, { useState, useEffect, useCallback, useRef } from "react";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";
import host from "../../global";
import { toast } from "react-toastify";
import {
  Code2,
  CircleCheck,
  CircleX,
  ChevronDown,
  ChevronUp,
  X,
  TriangleAlert,
} from "lucide-react";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useTheme } from "../../hooks/useTheme";
import ApexDescription from "../common/ApexDescription";
import ApexRubricResult, { ApexScoreChip } from "./ApexRubricResult";
import {
  isApexProblem, summarizeApex, hasAnyPending, hasStalePending,
} from "./apexReport";

const APEX_POLL_MS = 5000;
const APEX_MAX_POLLS = 36; // ~3 minutes, then the "retry" path takes over

const formatStartTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d)) return raw;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const getScorePathColor = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 60) return "#34B53A";
  if (n >= 30) return "#FFB200";
  return "#DC0000";
};

const getScoreTrailColor = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 60) return "rgba(52,181,58,0.15)";
  if (n >= 30) return "rgba(255,178,0,0.15)";
  return "rgba(220,0,0,0.15)";
};

export default function CodingTestReport({ name, coding_percent, interviewId = null, data = undefined }) {
  const { interview_id: routeInterviewId } = useParams();
  // Public share-token route: the parent passes the real id (the route param is the
  // token) and pre-fetched `data` (the authed problems/codesubmissions routes are unavailable).
  const interview_id = interviewId || routeInterviewId;
  const { theme: isDarkMode } = useTheme();
  const [problems, setProblems] = useState([]);
  const [codeSubmissions, setCodeSubmissions] = useState([]);
  const [startDateTime, setStartDateTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProblems, setExpandedProblems] = useState(new Set());
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const pollCountRef = useRef(0);
  const autoRetriedRef = useRef(false);
  const accessToken = sessionStorage.getItem("accessToken");

  const toggleProblemExpanded = (problemId) => {
    setExpandedProblems((prev) => {
      const next = new Set(prev);
      if (next.has(problemId)) next.delete(problemId);
      else next.add(problemId);
      return next;
    });
  };

  const handleToggleDetails = (problemId) => {
    setSelectedProblemId((prev) => (prev === problemId ? null : problemId));
  };

  useEffect(() => {
    if (data) {
      // Pre-fetched, already-merged problem+submission list from the public
      // report payload -- reshape it into the same { problems, codeSubmissions }
      // split the authenticated fetch below produces, so the render logic
      // further down never has to know which path it came from.
      setProblems(data.map((d) => ({
        id: d.id, title: d.title, description: d.description,
        track: d.track, topics: d.topics,
      })));
      setCodeSubmissions(Object.fromEntries(data.map((d) => [d.id, {
        language: d.language, code: d.code, is_submitted: true,
        evaluation_status: d.evaluation_status, coding_score: d.coding_score,
        rubric_scores: d.rubric_scores, overall_remark: d.overall_remark,
      }])));
      setLoading(false);
      return;
    }

    const fetchProblems = async () => {
      try {
        const response = await axios.get(
          `${host}/api/problems_from_codesubmissions`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { interview_id },
          }
        );
        setProblems(response.data);
      } catch (error) {
        if (error.response) {
          const { status, data: { detail } } = error.response;
          // Don't clear the login cookie here: a 401 on this one panel's
          // fetch isn't proof the admin's whole session is invalid, and every
          // portal shell re-checks this cookie on the next navigation and
          // force-signs-out the instant it's gone -- so wiping it from a
          // single report tab was logging the admin out of the entire app.
          if (status === 401) { toast.error(`Unauthorized: ${detail}`); }
          else if (status === 404) toast.warning(`Not Found: ${detail}`);
          else toast.error(`Error: ${detail}`);
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
      }
    };

    const fetchCodeSubmissions = async () => {
      try {
        const response = await axios.get(`${host}/api/get_codesubmissions`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { interview_id },
        });
        setCodeSubmissions(response.data.submissions || {});
        setStartDateTime(response.data.startDateTime || null);
      } catch (error) {
        if (error.response) {
          const { status, data: { detail } } = error.response;
          // Don't clear the login cookie here: a 401 on this one panel's
          // fetch isn't proof the admin's whole session is invalid, and every
          // portal shell re-checks this cookie on the next navigation and
          // force-signs-out the instant it's gone -- so wiping it from a
          // single report tab was logging the admin out of the entire app.
          if (status === 401) { toast.error(`Unauthorized: ${detail}`); }
          else if (status === 404) toast.warning(`Not Found: ${detail}`);
          else toast.error(`Error: ${detail}`);
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
      }
    };

    const load = async () => {
      setLoading(true);
      await fetchProblems();
      await fetchCodeSubmissions();
      setLoading(false);
    };

    load();
  }, [interview_id, accessToken, data]);

  // ── Apex: silent refresh used by polling and by "Retry review" ───────────────
  const refreshSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(`${host}/api/get_codesubmissions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { interview_id },
      });
      setCodeSubmissions(response.data.submissions || {});
    } catch {
      /* keep what is on screen; the next poll may succeed */
    }
  }, [interview_id, accessToken]);

  const retryApexReview = useCallback(async () => {
    setRetrying(true);
    try {
      await axios.post(`${host}/api/apex/reevaluate/${interview_id}`, null, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      pollCountRef.current = 0; // start polling again
      await refreshSubmissions();
    } catch {
      toast.error("Could not restart the AI review. Please try again.");
    } finally {
      setRetrying(false);
    }
  }, [interview_id, accessToken, refreshSubmissions]);

  // Keep the page fresh while AI reviews finish in the background.
  useEffect(() => {
    if (loading || !hasAnyPending(problems, codeSubmissions)) return undefined;
    if (pollCountRef.current >= APEX_MAX_POLLS) return undefined;
    const t = setTimeout(() => { pollCountRef.current += 1; refreshSubmissions(); }, APEX_POLL_MS);
    return () => clearTimeout(t);
  }, [loading, problems, codeSubmissions, refreshSubmissions]);

  // A review still "pending" long after submission was lost (e.g. server restart): restart it once.
  useEffect(() => {
    if (loading || !accessToken || autoRetriedRef.current) return;
    if (hasStalePending(problems, codeSubmissions)) {
      autoRetriedRef.current = true;
      retryApexReview();
    }
  }, [loading, accessToken, problems, codeSubmissions, retryApexReview]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <CircularProgress size={32} sx={{ color: "#C9A3FF" }} />
      </div>
    );
  }

  let totalTCPassed = 0;
  let totalTCCount = 0;
  const solvedTitles = [];
  const needsImprovementTitles = [];
  // Apex rounds are AI-reviewed against a rubric (no test cases), so they are scored from the review.
  const apexMode = problems.length > 0 && problems.every(isApexProblem);
  const apexSummary = apexMode ? summarizeApex(problems, codeSubmissions) : null;
  if (apexMode) {
    solvedTitles.push(...apexSummary.solved);
    needsImprovementTitles.push(...apexSummary.needs);
  }
  problems.forEach((p) => {
    if (apexMode) return;
    const s = codeSubmissions[p.id];
    if (!s) {
      needsImprovementTitles.push(p.title);
      return;
    }
    const results = s.test_case_results;
    let passed = 0;
    let total = 0;
    if (Array.isArray(results) && results.length > 0) {
      total = results.length;
      passed = results.filter((tc) => tc.passed).length;
    } else if (typeof s.test_cases_passed_count === "number" && typeof s.test_cases_total === "number") {
      total = s.test_cases_total;
      passed = s.test_cases_passed_count;
    }
    totalTCCount += total;
    totalTCPassed += passed;
    if (total > 0 && passed === total) {
      solvedTitles.push(p.title);
    } else {
      needsImprovementTitles.push(p.title);
    }
  });
  const scoreVal = apexMode ? (apexSummary.avg ?? 0) : (totalTCCount > 0 ? (totalTCPassed / totalTCCount) * 100 : 0);
  const hasScore = apexMode ? apexSummary.avg !== null : totalTCCount > 0;

  const attemptedCount = problems.filter((p) => {
    const s = codeSubmissions[p.id];
    return s?.is_submitted ?? (s?.language && s?.code);
  }).length;

  return (
    <div className="space-y-4 pb-6">

      {/* ── Overview Stats ── */}
      <div className="flex items-stretch gap-8 px-1 py-2">
        {hasScore && (
          <>
            <div className="flex flex-col items-center justify-center flex-shrink-0">
              <div className="relative" style={{ width: 160, height: 160 }}>
                <CircularProgressbar
                  value={scoreVal}
                  text={`${Math.floor(scoreVal)}%`}
                  circleRatio={0.75}
                  styles={buildStyles({
                    rotation: 1 / 2 + 1 / 8,
                    strokeLinecap: "butt",
                    pathColor: getScorePathColor(scoreVal),
                    trailColor: getScoreTrailColor(scoreVal),
                    textColor: isDarkMode === "dark" ? "#FFFFFF" : "#1F2937",
                    textSize: "24px",
                    fontWeight: "600",
                  })}
                />
                <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                  Score
                </div>
              </div>
            </div>

            <div className={`w-px self-stretch ${isDarkMode === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200'}`} />
          </>
        )}

        <div className="flex-1 flex flex-col justify-center gap-4 min-w-0">
          {/* Quick meta line */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex flex-col leading-tight">
              <span>
                <span className="text-2xl font-medium text-gray-500 dark:text-gray-400">
                  {attemptedCount}
                </span>
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  /{problems.length}
                </span>
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Attempted
              </span>
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            <span className="flex flex-col leading-tight">
              <span>
                <span className="text-2xl font-medium text-gray-500 dark:text-gray-400">
                  {apexMode ? apexSummary.reviewed : totalTCPassed}
                </span>
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  /{apexMode ? problems.length : totalTCCount}
                </span>
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {apexMode ? "AI-Reviewed" : "Test Cases Passed"}
              </span>
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            <span className="flex flex-col leading-tight">
              {/* <span className="text-2xl font-medium text-gray-500 dark:text-gray-400">
                {formatStartTime(startDateTime)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Test Start Time
              </span> */}
            </span>
          </div>

          {apexMode && (apexSummary.pending > 0 || apexSummary.failed > 0) && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {apexSummary.pending > 0 && `${apexSummary.pending} answer${apexSummary.pending > 1 ? "s are" : " is"} still being reviewed. `}
              {apexSummary.failed > 0 && `${apexSummary.failed} review${apexSummary.failed > 1 ? "s" : ""} failed. `}
              The score covers reviewed answers only.
            </p>
          )}

          {solvedTitles.length > 0 && (
            <div>
              <div className="flex items-center">
                <CircleCheck className="w-5 h-5 text-green-600 dark:text-[#C9A3FF] flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-[#C9A3FF] ml-2">
                  Problems Solved
                </span>
                <div className={`ml-3 flex-1 border-t ${isDarkMode === 'dark' ? 'border-gray-700/50' : 'border-gray-200'}`} />
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {solvedTitles.map((title, i) => (
                  <span
                    key={i}
                    className="text-sm px-4 py-2 rounded-full border border-green-500/30 bg-green-50 text-green-600 dark:border-[rgba(0,255,0,0.3)] dark:bg-[rgba(0,255,0,0.1)] dark:text-[#34B53A]"
                  >
                    {title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {needsImprovementTitles.length > 0 && (
            <div>
              <div className="flex items-center">
                <TriangleAlert className="w-5 h-5 text-amber-600 dark:text-[#C9A3FF] flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-[#C9A3FF] ml-2">
                  Needs Improvement
                </span>
                <div className={`ml-3 flex-1 border-t ${isDarkMode === 'dark' ? 'border-gray-700/50' : 'border-gray-200'}`} />
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {needsImprovementTitles.map((title, i) => (
                  <span
                    key={i}
                    className="text-sm px-4 py-2 rounded-full border border-orange-500/30 bg-orange-50 text-orange-600 dark:border-[rgba(245,158,11,0.3)] dark:bg-[rgba(245,158,11,0.1)] dark:text-[#f59e0b]"
                  >
                    {title}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* ── Problem Cards ── */}
      {problems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Code2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-[#A8A8A8]">
            No coding problems found for this assessment.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {problems.map((problem, index) => {
            const problemId = problem.id;
            const submission = codeSubmissions[problemId];
            const isApex = isApexProblem(problem);
            const testCaseResults = submission?.test_case_results;
            const hasTestCaseResults =
              Array.isArray(testCaseResults) && testCaseResults.length > 0;
            const totalTestCasesCount =
              submission?.test_cases_total ??
              (hasTestCaseResults ? testCaseResults.length : 0);
            const acceptedTestCasesCount =
              submission?.test_cases_passed_count ??
              (hasTestCaseResults
                ? testCaseResults.filter((tc) => tc.passed).length
                : 0);
            const isSubmitted = Boolean(
              submission?.is_submitted ?? (submission?.language && submission?.code)
            );
            const isExpanded = expandedProblems.has(problemId);
            const isTestDetailsOpen = selectedProblemId === problemId;
            const allPassed =
              totalTestCasesCount > 0 &&
              acceptedTestCasesCount === totalTestCasesCount;

            const tcBadgeClass = allPassed
              ? isDarkMode === 'dark'
                ? "bg-green-400/10 text-green-400 border-green-400/30"
                : "bg-green-50 text-green-600 border-green-200"
              : acceptedTestCasesCount > 0
              ? isDarkMode === 'dark'
                ? "bg-amber-400/10 text-amber-400 border-amber-400/30"
                : "bg-amber-50 text-amber-600 border-amber-200"
              : isDarkMode === 'dark'
                ? "bg-red-400/10 text-red-400 border-red-400/30"
                : "bg-red-50 text-red-600 border-red-200";

            return (
              <div
                key={problem._id ?? index}
                className={`rounded-2xl border overflow-hidden transition-colors ${
                  isExpanded
                    ? isDarkMode === 'dark' ? 'border-gray-700/50 bg-white/5' : 'border-gray-200 bg-white'
                    : isDarkMode === 'dark' ? 'border-gray-700/40 bg-transparent' : 'border-gray-200 bg-transparent'
                }`}
              >
                {/* ── Card Header (always visible) ── */}
                <button
                  onClick={() => toggleProblemExpanded(problemId)}
                  className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors ${isDarkMode === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                >
                  {/* Problem number badge */}
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-400/15 text-violet-700 dark:text-violet-300 text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>

                  {/* Title */}
                  <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-white text-left">
                    {problem.title}
                  </span>

                  {/* Status chips */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isSubmitted ? (
                      <>
                        {submission.language && (
                          <span className={`text-sm px-2.5 py-1 rounded-full border font-medium ${isDarkMode === 'dark' ? 'bg-blue-400/10 text-blue-300 border-blue-400/25' : 'bg-blue-50 text-blue-600 border-blue-200/60'}`}>
                            {submission.language}
                          </span>
                        )}
                        {isApex && <ApexScoreChip submission={submission} isDark={isDarkMode === 'dark'} />}
                        {!isApex && totalTestCasesCount > 0 && (
                          <span
                            className={`text-sm px-3 py-1 rounded-full font-semibold border ${tcBadgeClass}`}
                          >
                            {acceptedTestCasesCount}/{totalTestCasesCount} passed
                          </span>
                        )}
                      </>
                    ) : (
                      <span className={`text-sm px-3 py-1 rounded-full border ${isDarkMode === 'dark' ? 'bg-gray-700/50 text-gray-400 border-gray-600/50' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                        Not Attempted
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                </button>

                {/* ── Expanded Content ── */}
                {isExpanded && (
                  <div className={`border-t px-5 py-4 space-y-4 ${isDarkMode === 'dark' ? 'border-gray-700/40' : 'border-gray-100'}`}>

                    {/* Problem details */}
                    <div className="space-y-3 text-sm">
                      {problem.description && (
                        <div>
                          <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF] mb-1">
                            Description
                          </p>
                          {isApex ? (
                            <ApexDescription text={problem.description} textClassName="text-gray-700 dark:text-[#A8A8A8] leading-relaxed" codeClassName="bg-gray-900 text-green-300" />
                          ) : (
                            <p className="text-gray-700 dark:text-[#A8A8A8] leading-relaxed">
                              {problem.description}
                            </p>
                          )}
                        </div>
                      )}
                      {(problem.input_format || problem.output_format) && (
                        <div className="grid grid-cols-2 gap-3">
                          {problem.input_format && (
                            <div>
                              <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF] mb-1">
                                Input Format
                              </p>
                              <p className="text-gray-700 dark:text-[#A8A8A8]">
                                {problem.input_format}
                              </p>
                            </div>
                          )}
                          {problem.output_format && (
                            <div>
                              <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF] mb-1">
                                Output Format
                              </p>
                              <p className="text-gray-700 dark:text-[#A8A8A8]">
                                {problem.output_format}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      {problem.constraints && (
                        <div>
                          <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF] mb-1">
                            Constraints
                          </p>
                          <p className="text-gray-700 dark:text-[#A8A8A8] font-mono text-sm">
                            {problem.constraints}
                          </p>
                        </div>
                      )}
                      {problem.examples?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF] mb-2">
                            Examples
                          </p>
                          <div className="space-y-2">
                            {problem.examples.map((ex, exIdx) => (
                              <div
                                key={exIdx}
                                className={`rounded-xl border px-4 py-2.5 text-sm font-mono space-y-0.5 ${isDarkMode === 'dark' ? 'bg-white/5 border-gray-700/30' : 'bg-gray-50 border-gray-100'}`}
                              >
                                <div>
                                  <span className="text-gray-400 dark:text-gray-500">
                                    Input:{" "}
                                  </span>
                                  <span className="text-gray-700 dark:text-gray-300">
                                    {ex.input}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-400 dark:text-gray-500">
                                    Output:{" "}
                                  </span>
                                  <span className="text-gray-700 dark:text-gray-300">
                                    {ex.output}
                                  </span>
                                </div>
                                {ex.explanation && (
                                  <div className="pt-0.5">
                                    <span className="text-gray-400 dark:text-gray-500">
                                      Explanation:{" "}
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-400 font-sans">
                                      {ex.explanation}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Submission section */}
                    {isSubmitted ? (
                      <div className="space-y-4">
                        <div className={`h-px ${isDarkMode === 'dark' ? 'bg-gray-700/40' : 'bg-gray-100'}`} />

                        {/* Meta row */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1.5">
                            <Code2 className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                            <span className="font-semibold text-blue-600 dark:text-blue-300">
                              {submission.language}
                            </span>
                          </div>
                          {submission.submitted_at && (
                            <span className="text-gray-400 dark:text-gray-500">
                              Submitted{" "}
                              {new Date(submission.submitted_at).toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </span>
                          )}
                        </div>

                        {/* Code block */}
                        <div>
                          <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF] mb-2">
                            Submitted Code
                          </p>
                          <pre
                            className={`rounded-xl text-sm px-4 py-3 overflow-x-auto leading-relaxed border max-h-64 overflow-y-auto ${isDarkMode === 'dark' ? 'bg-gray-900 text-gray-100 border-gray-700/60' : 'bg-gray-100 text-gray-800 border-gray-200'}`}
                            style={{
                              fontFamily:
                                "'Fira Code','JetBrains Mono','Courier New',monospace",
                            }}
                          >
                            {submission.code}
                          </pre>
                        </div>

                        {/* Apex: AI rubric review instead of test cases */}
                        {isApex && (
                          <ApexRubricResult
                            submission={submission}
                            isDark={isDarkMode === 'dark'}
                            canRetry={Boolean(accessToken)}
                            retrying={retrying}
                            onRetry={retryApexReview}
                          />
                        )}

                        {/* Test case summary */}
                        {totalTestCasesCount > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF]">
                                Test Cases
                              </p>
                              <span
                                className={`text-sm font-bold ${
                                  allPassed
                                    ? "text-green-600 dark:text-green-400"
                                    : acceptedTestCasesCount > 0
                                    ? "text-amber-600 dark:text-amber-400"
                                    : "text-red-600 dark:text-red-400"
                                }`}
                              >
                                {acceptedTestCasesCount}/{totalTestCasesCount} Passed
                              </span>
                            </div>

                            {/* Pass/fail mini grid */}
                            {hasTestCaseResults && (
                              <div className="flex flex-wrap gap-1.5">
                                {testCaseResults.map((tc, tcIdx) => (
                                  <div
                                    key={tcIdx}
                                    title={tc.status_message || (tc.passed ? "Accepted" : "Failed")}
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center border text-sm font-bold cursor-default ${
                                      tc.passed
                                        ? isDarkMode === 'dark' ? 'bg-green-400/15 border-green-400/40 text-green-400' : 'bg-green-50 border-green-300 text-green-600'
                                        : isDarkMode === 'dark' ? 'bg-red-400/15 border-red-400/40 text-red-400' : 'bg-red-50 border-red-300 text-red-600'
                                    }`}
                                  >
                                    {tc.passed ? "✓" : "✗"}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Toggle test case details */}
                        {hasTestCaseResults && (
                          <button
                            onClick={() => handleToggleDetails(problemId)}
                            className="flex items-center gap-1.5 text-sm font-medium text-purple-600 dark:text-[#C9A3FF] hover:text-purple-800 dark:hover:text-purple-300 transition-colors"
                          >
                            {isTestDetailsOpen ? (
                              <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                            {isTestDetailsOpen
                              ? "Hide Test Case Details"
                              : "View Full Test Case Details"}
                          </button>
                        )}

                        {/* Full test case details */}
                        {isTestDetailsOpen && hasTestCaseResults && (
                          <div className={`rounded-xl border overflow-hidden ${isDarkMode === 'dark' ? 'border-gray-700/40' : 'border-gray-200'}`}>
                            {testCaseResults.map((tc, tcIdx) => (
                              <div
                                key={tcIdx}
                                className={`px-4 py-3.5 border-b last:border-b-0 ${
                                  isDarkMode === 'dark'
                                    ? `border-gray-700/30 ${tc.passed ? 'bg-transparent' : 'bg-transparent'}`
                                    : `border-gray-100 ${tc.passed ? 'bg-transparent' : 'bg-transparent'}`
                                }`}
                              >
                                {/* Test case header */}
                                <div className="flex items-center gap-2 mb-3">
                                  {tc.passed ? (
                                    <CircleCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <CircleX className="w-4 h-4 text-red-500 flex-shrink-0" />
                                  )}
                                  <span
                                    className={`text-sm font-semibold ${
                                      tc.passed
                                        ? "text-green-700 dark:text-green-400"
                                        : "text-red-700 dark:text-red-400"
                                    }`}
                                  >
                                    Test Case {tcIdx + 1} —{" "}
                                    {tc.passed ? "Passed" : "Failed"}
                                  </span>
                                  {tc.status_message && (
                                    <span className="text-sm text-gray-400 dark:text-gray-500">
                                      ({tc.status_message})
                                    </span>
                                  )}
                                </div>

                                <div className="space-y-2 text-sm">
                                  {tc.input && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-500 mb-0.5 font-medium">
                                        Input
                                      </p>
                                      <pre className={`rounded-lg px-3 py-2 overflow-x-auto text-sm leading-relaxed ${isDarkMode === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                        {tc.input}
                                      </pre>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-2 gap-2 items-stretch">
                                    <div className="flex flex-col">
                                      <p
                                        className={`mb-0.5 font-medium ${
                                          tc.passed
                                            ? "text-green-600 dark:text-green-400"
                                            : "text-red-500 dark:text-red-400"
                                        }`}
                                      >
                                        Your Output
                                      </p>
                                      <pre
                                        className={`flex-1 rounded-lg px-3 py-2 overflow-x-auto text-sm leading-relaxed ${
                                          tc.passed
                                            ? isDarkMode === 'dark' ? 'bg-green-900/25 text-green-200' : 'bg-green-100 text-green-800'
                                            : isDarkMode === 'dark' ? 'bg-red-900/25 text-red-200' : 'bg-red-100 text-red-800'
                                        }`}
                                      >
                                        {tc.yourOutput}
                                      </pre>
                                    </div>
                                    <div className="flex flex-col">
                                      <p className="text-gray-500 dark:text-gray-400 mb-0.5 font-medium">
                                        Expected Output
                                      </p>
                                      <pre className={`flex-1 rounded-lg px-3 py-2 overflow-x-auto text-sm leading-relaxed ${isDarkMode === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                        {tc.expectedOutput}
                                      </pre>
                                    </div>
                                  </div>
                                  {tc.stderr && (
                                    <div>
                                      <p className="text-amber-500 dark:text-amber-400 mb-0.5 font-medium">
                                        Stderr
                                      </p>
                                      <pre className={`rounded-lg px-3 py-2 overflow-x-auto text-sm leading-relaxed ${isDarkMode === 'dark' ? 'bg-amber-900/20 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
                                        {tc.stderr}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`flex items-center gap-2.5 py-3 px-4 rounded-xl border border-dashed ${isDarkMode === 'dark' ? 'bg-white/5 border-gray-700/40' : 'bg-gray-50 border-gray-200'}`}>
                        <X className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-gray-500 dark:text-[#A8A8A8]">
                          This problem was not attempted
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
